import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Play, Pause, Volume2, VolumeX, ChevronRight, ChevronLeft, Camera, BarChart3, Calendar, Flag, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OnboardingModal = ({ isOpen, onClose, token }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);

  const slides = [
    {
      title: "Welcome!",
      subtitle: "Your personal nutrition tracking companion",
      description: "Let's take a quick tour of how to use your dashboard to track your meals and monitor your health.",
      icon: Sparkles,
      color: "from-primary to-[#8FD14F]",
      tips: [
        "Track your daily meals with AI-powered analysis",
        "Monitor your macros and micronutrients",
        "Get personalized health insights"
      ]
    },
    {
      title: "Upload Your Meals",
      subtitle: "Food Diary Tab",
      description: "Take a photo of your meal and our AI will analyze it instantly, identifying the food and estimating nutritional content.",
      icon: Camera,
      color: "from-orange-500 to-amber-500",
      tips: [
        "Click the '+' button to upload a meal photo",
        "Choose meal type: Breakfast, Lunch, Dinner, or Snack",
        "AI analyzes protein, carbs, fat, and sugar automatically"
      ]
    },
    {
      title: "View Detailed Analysis",
      subtitle: "Click on any meal",
      description: "Tap on any meal in your food diary to see the detailed AI analysis including macronutrients and micronutrients.",
      icon: BarChart3,
      color: "from-blue-500 to-cyan-500",
      tips: [
        "Click 'Analyze Micronutrients' for vitamins & minerals",
        "Edit the analysis if AI made a mistake",
        "Finalize entries to lock in the nutritional data"
      ]
    },
    {
      title: "Track Your Biometrics",
      subtitle: "Biometrics Tab",
      description: "Set up your body measurements to get personalized calorie targets and track your progress over time.",
      icon: BarChart3,
      color: "from-purple-500 to-pink-500",
      tips: [
        "Enter your height, weight, and waist measurements",
        "See your BMI and ideal body weight calculated",
        "Track daily macros against your personal targets"
      ]
    },
    {
      title: "Weekly Reports",
      subtitle: "Weekly Report Tab",
      description: "Get a comprehensive summary of your nutrition for the week with charts and downloadable reports.",
      icon: Calendar,
      color: "from-green-500 to-emerald-500",
      tips: [
        "View daily calorie and macro breakdowns",
        "See trends over the past 7 days",
        "Download reports as PDF or Word"
      ]
    },
    {
      title: "My Health Flags",
      subtitle: "My Flags Tab",
      description: "Get AI-powered insights about potential nutritional deficiencies or excesses based on your eating patterns.",
      icon: Flag,
      color: "from-red-500 to-rose-500",
      tips: [
        "Analyzes your last 2 weeks of meals",
        "Identifies nutrients you may be lacking",
        "Provides actionable recommendations"
      ]
    }
  ];

  // Auto-advance slides
  useEffect(() => {
    if (!isOpen || !isPlaying) return;
    
    const slideInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          // Move to next slide
          setCurrentSlide((current) => {
            if (current >= slides.length - 1) {
              setIsPlaying(false);
              return current;
            }
            return current + 1;
          });
          return 0;
        }
        return prev + 2; // 50 steps over ~10 seconds per slide
      });
    }, 200);

    return () => clearInterval(slideInterval);
  }, [isOpen, isPlaying, slides.length]);

  // Reset progress when slide changes
  useEffect(() => {
    setProgress(0);
  }, [currentSlide]);

  const handleComplete = async () => {
    try {
      await axios.post(
        `${API_URL}/api/auth/complete-onboarding`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Failed to mark onboarding complete:', error);
    }
    onClose();
  };

  const handleSkip = async () => {
    await handleComplete();
  };

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
      setProgress(0);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
      setProgress(0);
    }
  };

  const currentSlideData = slides[currentSlide];
  const IconComponent = currentSlideData.icon;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={(e) => e.target === e.currentTarget && handleSkip()}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="bg-card rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden"
          data-testid="onboarding-modal"
        >
          {/* Header with progress */}
          <div className="relative">
            {/* Progress bar */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-muted">
              <div className="flex gap-1 h-full px-1">
                {slides.map((slide, index) => (
                  <div key={`progress-${slide.title}`} className="flex-1 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full bg-gradient-to-r ${currentSlideData.color} transition-all duration-200`}
                      style={{
                        width: index < currentSlide ? '100%' : index === currentSlide ? `${progress}%` : '0%'
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Close/Skip button */}
            <button
              onClick={handleSkip}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-muted/50 transition-colors z-10"
              data-testid="onboarding-skip"
            >
              <X className="w-5 h-5 text-muted-foreground" />
            </button>

            {/* Slide content */}
            <div className="pt-8 px-8 pb-6">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentSlide}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Icon */}
                  <div className={`w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-r ${currentSlideData.color} flex items-center justify-center shadow-lg`}>
                    <IconComponent className="w-10 h-10 text-white" />
                  </div>

                  {/* Title */}
                  <h2 className="text-2xl font-bold text-center mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {currentSlideData.title}
                  </h2>
                  <p className={`text-center text-sm font-semibold bg-gradient-to-r ${currentSlideData.color} bg-clip-text text-transparent mb-4`}>
                    {currentSlideData.subtitle}
                  </p>

                  {/* Description */}
                  <p className="text-center text-muted-foreground mb-6">
                    {currentSlideData.description}
                  </p>

                  {/* Tips */}
                  <div className="bg-muted/50 rounded-2xl p-5 space-y-3">
                    {currentSlideData.tips.map((tip, index) => (
                      <motion.div
                        key={`tip-${currentSlide}-${index}`}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="flex items-start gap-3"
                      >
                        <div className={`w-6 h-6 rounded-full bg-gradient-to-r ${currentSlideData.color} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                          <span className="text-white text-xs font-bold">{index + 1}</span>
                        </div>
                        <p className="text-sm text-foreground">{tip}</p>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Footer with navigation */}
          <div className="px-8 pb-8 pt-2">
            <div className="flex items-center justify-between">
              {/* Play/Pause and slide indicator */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setIsPlaying(!isPlaying)}
                  className="p-2 rounded-full hover:bg-muted transition-colors"
                  data-testid="onboarding-play-pause"
                >
                  {isPlaying ? (
                    <Pause className="w-5 h-5 text-muted-foreground" />
                  ) : (
                    <Play className="w-5 h-5 text-muted-foreground" />
                  )}
                </button>
                <span className="text-sm text-muted-foreground">
                  {currentSlide + 1} / {slides.length}
                </span>
              </div>

              {/* Navigation buttons */}
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={prevSlide}
                  disabled={currentSlide === 0}
                  className="rounded-full px-4"
                  data-testid="onboarding-prev"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Back
                </Button>
                
                {currentSlide < slides.length - 1 ? (
                  <Button
                    size="sm"
                    onClick={nextSlide}
                    className={`rounded-full px-6 bg-gradient-to-r ${currentSlideData.color}`}
                    data-testid="onboarding-next"
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={handleComplete}
                    className="rounded-full px-6 bg-gradient-to-r from-primary to-[#8FD14F]"
                    data-testid="onboarding-complete"
                  >
                    Get Started!
                    <Sparkles className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default OnboardingModal;
